/** Header chips for PlanPage (plan modus + agenda). */
import { Box, Chip } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { SectionTabs } from '@/components/ui/SharedComponents';
import { HIGHLIGHT_PROJECT_FILTER_EVENT } from '@/components/layout/filterBarUtils';
import {
  FONT_WEIGHT_SEMIBOLD,
  FONT_WEIGHT_REGULAR,
} from '@/components/ui/taskVisualTokens';

function lockedChipSx(isLocked) {
  if (!isLocked) return {};
  return {
    opacity: 0.5,
    color: 'text.disabled',
    borderColor: 'text.disabled',
  };
}

function handlePlanModeChipClick(canUsePlanMode, onTogglePlanMode) {
  if (canUsePlanMode) {
    onTogglePlanMode();
    return;
  }
  window.dispatchEvent(new CustomEvent(HIGHLIGHT_PROJECT_FILTER_EVENT));
}

function planModeChipSx(planMode, canUsePlanMode) {
  return {
    flexShrink: 0,
    fontWeight: planMode ? FONT_WEIGHT_SEMIBOLD : FONT_WEIGHT_REGULAR,
    ...lockedChipSx(!canUsePlanMode),
  };
}

function agendaChipIcon(canUseAgenda) {
  if (canUseAgenda) {
    return <EventOutlinedIcon sx={{ fontSize: 16 }} />;
  }
  return <LockOutlinedIcon sx={{ fontSize: 16 }} />;
}

function agendaChipSx(isAgendaEnabled, canUseAgenda, syncStatus) {
  const sx = {
    flexShrink: 0,
    fontWeight: isAgendaEnabled
      ? FONT_WEIGHT_SEMIBOLD
      : FONT_WEIGHT_REGULAR,
    ...lockedChipSx(!canUseAgenda),
  };
  if (syncStatus?.error) {
    sx.borderColor = 'warning.main';
    sx.color = 'warning.main';
  }
  return sx;
}

/**
 * @param {object} props
 */
export function PlanPageHeader({
  tabs,
  activeView,
  onTabChange,
  planMode,
  canUsePlanMode,
  onTogglePlanMode,
  planModeLabel,
  planModeTitle,
  isAgendaEnabled,
  canUseAgenda = true,
  showAgendaToggle = true,
  onToggleAgenda,
  agendaLabel,
  agendaTitle,
  syncStatus,
}) {
  const showPlanModeToggle = activeView !== 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <SectionTabs
        tabs={tabs}
        value={activeView}
        onChange={onTabChange}
        sx={{ mb: 0, flex: 1, minWidth: 0 }}
      />
      {showPlanModeToggle ? (
        <Chip
          icon={<FormatListNumberedIcon sx={{ fontSize: 16 }} />}
          label={planModeLabel}
          size="small"
          variant={planMode ? 'filled' : 'outlined'}
          onClick={() =>
            handlePlanModeChipClick(canUsePlanMode, onTogglePlanMode)
          }
          title={planModeTitle}
          aria-pressed={planMode}
          aria-disabled={!canUsePlanMode}
          data-testid="plan-mode-toggle"
          sx={planModeChipSx(planMode, canUsePlanMode)}
        />
      ) : null}
      {showAgendaToggle ? (
        <Chip
          icon={agendaChipIcon(canUseAgenda)}
          label={agendaLabel}
          size="small"
          variant={isAgendaEnabled ? 'filled' : 'outlined'}
          onClick={onToggleAgenda}
          title={agendaTitle}
          aria-pressed={isAgendaEnabled}
          aria-disabled={!canUseAgenda}
          data-testid="plan-agenda-toggle"
          sx={agendaChipSx(isAgendaEnabled, canUseAgenda, syncStatus)}
        />
      ) : null}
    </Box>
  );
}
